/**
 * The Quality tab's accessibility check, run in the browser: each running web service's preview is same-origin
 * (`/api/preview/<project>/services/<svc>/proxy/`), so the page loads it in a hidden iframe, injects axe-core (an npm
 * dependency of this app, bundled and code-split, never from a CDN), runs it on the frame's document and keeps the
 * violations: rule, impact, help and its link, and up to 3 selectors per element. Never the elements' HTML or any text
 * of the page (it may hold personal data). The caller POSTs the result to vfs-service, which stores it with the run.
 *
 * Bounds: at most 10 pages, one at a time, `timeoutMs` per page (load and run), at most 100 violations per page, 20
 * elements per violation. A page on another origin, or one whose Content-Security-Policy blocks the injected script,
 * is reported as an error with the reason.
 */

export const MAX_PAGES = 10;
const MAX_VIOLATIONS = 100;
const MAX_NODES = 20;
const MAX_SELECTORS = 3;
const TAGS = ['wcag2a', 'wcag2aa', 'wcag21a', 'wcag21aa', 'wcag22aa', 'best-practice'];

export const CROSS_ORIGIN = 'The preview is on another address, so it cannot be checked from this page.';
export const CSP_BLOCKED = 'The page blocks scripts from Flow (its Content-Security-Policy), so it cannot be checked.';

class TimeoutError extends Error {}

const cut = (value, max) => (typeof value === 'string' ? value.slice(0, max) : '');

function withTimeout(promise, ms) {
  let timer;
  const limit = new Promise((resolve, reject) => { timer = setTimeout(() => reject(new TimeoutError('timeout')), ms); });
  return Promise.race([promise, limit]).finally(() => clearTimeout(timer));
}

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/** One axe violation, trimmed: no HTML, no page text. */
function trimViolation(v) {
  return {
    id: cut(v.id, 100),
    impact: v.impact || null,
    help: cut(v.help, 300),
    helpUrl: cut(v.helpUrl, 500),
    description: cut(v.description, 500),
    tags: (Array.isArray(v.tags) ? v.tags : []).slice(0, 8).map((t) => cut(String(t), 60)),
    nodes: (Array.isArray(v.nodes) ? v.nodes : []).slice(0, MAX_NODES).map((n) => ({
      target: (Array.isArray(n.target) ? n.target : []).slice(0, MAX_SELECTORS).map((t) => cut(Array.isArray(t) ? t.join(' ') : String(t), 300)),
      failureSummary: cut(n.failureSummary, 500),
    })),
  };
}

function waitForLoad(frame, ms) {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    frame.addEventListener('load', () => { clearTimeout(timer); resolve(); }, { once: true });
  });
}

function hiddenFrame(doc, url, service) {
  const frame = doc.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('tabindex', '-1');
  frame.title = `Accessibility check of ${service}`;
  frame.style.cssText = 'position:fixed;left:-10000px;top:0;width:1280px;height:800px;visibility:hidden;border:0';
  frame.src = url;
  return frame;
}

/** Puts axe into the frame: a script element first, the frame's Function as a fallback. Answers the frame's axe. */
function inject(frame, axe) {
  const win = frame.contentWindow;
  const fdoc = frame.contentDocument;
  if (!win || !fdoc) { return null; }
  try {
    const script = fdoc.createElement('script');
    script.textContent = axe.source;
    (fdoc.head || fdoc.documentElement || fdoc.body).appendChild(script);
  } catch {
    // the fallback below
  }
  if (!win.axe) {
    try { win.Function(axe.source)(); } catch { /* blocked: reported below */ }
  }
  return win.axe || null;
}

async function checkPage({ target, axe, timeoutMs, settleMs, doc, origin, base }) {
  const { service } = target;
  let resolved;
  try {
    resolved = new URL(target.url, base);
  } catch {
    return { service, url: String(target.url || ''), status: 'error', message: 'The preview address is not valid.' };
  }
  const url = resolved.href;
  if (resolved.origin !== origin) { return { service, url, status: 'error', message: CROSS_ORIGIN }; }
  const frame = hiddenFrame(doc, url, service);
  try {
    const loaded = waitForLoad(frame, timeoutMs);
    doc.body.appendChild(frame);
    await loaded;
    if (settleMs > 0) { await sleep(settleMs); }
    const frameAxe = inject(frame, axe);
    if (!frameAxe || typeof frameAxe.run !== 'function') { return { service, url, status: 'error', message: CSP_BLOCKED }; }
    const result = await withTimeout(Promise.resolve(frameAxe.run(frame.contentDocument, { runOnly: { type: 'tag', values: TAGS }, resultTypes: ['violations'] })), timeoutMs);
    const violations = (result?.violations || []).slice(0, MAX_VIOLATIONS).map(trimViolation);
    return { service, url, status: 'done', title: cut(frame.contentDocument?.title || '', 120), violations };
  } catch (error) {
    if (error instanceof TimeoutError) { return { service, url, status: 'timeout', message: `The page did not answer within ${Math.round(timeoutMs / 1000)} s.` }; }
    return { service, url, status: 'error', message: cut(String(error?.message || error), 300) };
  } finally {
    frame.remove();
  }
}

/**
 * Runs axe-core on each target `{ service, url }` (at most 10, one at a time).
 * @returns {Promise<{ engine: { name, version }, pages, startedAt, durationMs }>}
 */
export async function runAccessibility({
  targets = [], loadAxe = () => import('axe-core'), timeoutMs = 30000, settleMs = 1500, onProgress = () => {}, doc = document, signal = null,
} = {}) {
  const startedAt = Date.now();
  const mod = await loadAxe();
  const axe = mod?.default || mod;
  const list = (targets || []).slice(0, MAX_PAGES);
  const pages = [];
  const origin = window.location.origin;
  const base = window.location.href;
  for (let index = 0; index < list.length; index += 1) {
    if (signal?.aborted) { break; }
    onProgress({ index, total: list.length, service: list[index].service });
    // eslint-disable-next-line no-await-in-loop
    pages.push(await checkPage({ target: list[index], axe, timeoutMs, settleMs, doc, origin, base }));
  }
  return { engine: { name: 'axe-core', version: axe?.version || null }, pages, startedAt, durationMs: Date.now() - startedAt };
}

/** Counts of violation elements by impact: `{ total, byImpact: { critical, serious, moderate, minor }, pages }`. */
export function summarizeA11y(result) {
  const byImpact = { critical: 0, serious: 0, moderate: 0, minor: 0 };
  let total = 0;
  const pages = result?.pages || [];
  pages.forEach((page) => {
    (page.violations || []).forEach((v) => {
      const n = Math.max(1, (v.nodes || []).length);
      total += n;
      if (byImpact[v.impact] !== undefined) { byImpact[v.impact] += n; }
    });
  });
  return { total, byImpact, pages: pages.length };
}
